<template>
  <div class="dashboard-card chart-card">
    <h2 class="section-title">{{ title }}</h2>
    <p v-if="subtitle" class="section-subtitle">{{ subtitle }}</p>
    <div v-if="hasChartData" class="chart-container">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <p v-else class="empty-state">{{ t('adminDashboard.noData') }}</p>
  </div>
</template>

<script setup lang="ts">
import type { AdminDashboardBucket, ChartDisplayMode } from '@/utils/adminDashboardUtils'
import { applyChartDisplayMode } from '@/utils/adminDashboardUtils'
import Chart from 'chart.js/auto'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  title: string
  subtitle?: string
  buckets: AdminDashboardBucket[]
  displayMode: ChartDisplayMode
}>()

const { t } = useI18n()

const chartCanvas = ref<HTMLCanvasElement | null>(null)
let chartInstance: Chart | null = null

const chartBuckets = computed(() => applyChartDisplayMode(props.buckets, props.displayMode))

const hasChartData = computed(() => chartBuckets.value.length > 0)

const destroyChart = () => {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
}

const drawChart = async () => {
  await nextTick()

  if (!chartCanvas.value || !hasChartData.value) {
    destroyChart()
    return
  }

  destroyChart()

  chartInstance = new Chart(chartCanvas.value, {
    type: 'bar',
    data: {
      labels: chartBuckets.value.map((bucket) => bucket.label),
      datasets: [
        {
          label: props.title,
          data: chartBuckets.value.map((bucket) => bucket.count),
          backgroundColor: '#3b82f6',
          borderRadius: 4
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `${props.title}: ${context.parsed.y ?? 0}`
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    }
  })
}

watch(
  () => [props.buckets, props.displayMode],
  () => {
    drawChart()
  },
  { deep: true }
)

onMounted(() => {
  drawChart()
})

onUnmounted(() => {
  destroyChart()
})
</script>

<style scoped>
.dashboard-card {
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  height: 100%;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: var(--p-text-color);
}

.section-subtitle {
  margin: 0 0 1rem 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.chart-card {
  min-height: 380px;
}

.chart-container {
  height: 320px;
  width: 100%;
}

.empty-state {
  margin: 2rem 0;
  text-align: center;
  color: var(--p-text-muted-color);
}
</style>
